import { useState, useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  useSysGetConfig,
  useSysSetConfig,
} from "@tubo/api-models/hooks/system-config-api.hooks";
import { Button } from "@tubo/ui/components/ui/button";
import { Input } from "@tubo/ui/components/ui/input";
import { Label } from "@tubo/ui/components/ui/label";
import { Switch } from "@tubo/ui/components/ui/switch";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  FALLBACK_LOGIN_DEFAULT_ROUTE,
  LOGIN_DEFAULT_ROUTE_CONFIG_KEY,
  normalizeLoginDefaultRoute,
  resolveLoginDefaultRoute,
} from "@client/lib/login-default-route";

const FRONTEND_URL_KEY = "frontend.url";
const ALLOW_SELF_REGISTRATION_KEY = "auth.allowSelfRegistration";
const ICP_BEIAN_NO_KEY = "site.icpBeianNo";
const POLICE_BEIAN_NO_KEY = "site.policeBeianNo";
const AGENT_TOOL_PYTHON_BIN_KEY = "agent-tool.pythonBin";

function SystemConfigPage() {
  const { t } = useTranslation("systemSettings");
  const { data: frontendUrlData, isLoading: isFrontendUrlLoading } =
    useSysGetConfig(FRONTEND_URL_KEY);
  const { data: loginDefaultRouteData, isLoading: isLoginDefaultRouteLoading } =
    useSysGetConfig(LOGIN_DEFAULT_ROUTE_CONFIG_KEY);
  const { data: allowRegistrationData, isLoading: isAllowRegistrationLoading } =
    useSysGetConfig(ALLOW_SELF_REGISTRATION_KEY);
  const { data: icpBeianData, isLoading: isIcpBeianLoading } =
    useSysGetConfig(ICP_BEIAN_NO_KEY);
  const { data: policeBeianData, isLoading: isPoliceBeianLoading } =
    useSysGetConfig(POLICE_BEIAN_NO_KEY);
  const setFrontendUrlMutation = useSysSetConfig({
    mutation: {
      onSuccess: () => {
        toast.success(t("common.saved"));
      },
      onError: () => {
        toast.error(t("common.saveFailed"));
      },
    },
  });
  const setLoginDefaultRouteMutation = useSysSetConfig({
    mutation: {
      onSuccess: () => {
        toast.success(t("common.saved"));
      },
      onError: () => {
        toast.error(t("common.saveFailed"));
      },
    },
  });
  const setAllowRegistrationMutation = useSysSetConfig({
    mutation: {
      onSuccess: () => {
        toast.success(t("common.saved"));
      },
      onError: () => {
        toast.error(t("common.saveFailed"));
      },
    },
  });
  const setBeianMutation = useSysSetConfig();

  const [frontendUrl, setFrontendUrl] = useState("");
  const [loginDefaultRoute, setLoginDefaultRoute] = useState(
    FALLBACK_LOGIN_DEFAULT_ROUTE,
  );
  // 未配置时默认允许注册（与后端 defaultValue 一致）
  const [allowRegistration, setAllowRegistration] = useState(true);
  const [icpBeianNo, setIcpBeianNo] = useState("");
  const [policeBeianNo, setPoliceBeianNo] = useState("");

  useEffect(() => {
    if (frontendUrlData) {
      setFrontendUrl(String(frontendUrlData.value ?? ""));
    }
  }, [frontendUrlData]);

  useEffect(() => {
    if (loginDefaultRouteData) {
      setLoginDefaultRoute(
        resolveLoginDefaultRoute(loginDefaultRouteData.value),
      );
    }
  }, [loginDefaultRouteData]);

  useEffect(() => {
    if (allowRegistrationData) {
      setAllowRegistration(allowRegistrationData.value !== "false");
    }
  }, [allowRegistrationData]);

  useEffect(() => {
    if (icpBeianData) {
      setIcpBeianNo(String(icpBeianData.value ?? ""));
    }
  }, [icpBeianData]);

  useEffect(() => {
    if (policeBeianData) {
      setPoliceBeianNo(String(policeBeianData.value ?? ""));
    }
  }, [policeBeianData]);

  const handleFrontendUrlSave = () => {
    setFrontendUrlMutation.mutate({
      key: FRONTEND_URL_KEY,
      data: { value: frontendUrl },
    });
  };

  const handleLoginDefaultRouteSave = () => {
    const normalizedRoute = normalizeLoginDefaultRoute(loginDefaultRoute);
    if (!normalizedRoute) {
      toast.error(t("system.invalidRoute"));
      return;
    }

    setLoginDefaultRoute(normalizedRoute);
    setLoginDefaultRouteMutation.mutate({
      key: LOGIN_DEFAULT_ROUTE_CONFIG_KEY,
      data: { value: normalizedRoute },
    });
  };

  const handleAllowRegistrationChange = (checked: boolean) => {
    // 始终同步开关显示值，仅对保存动作做乐观更新；失败时由 invalidate 回读真实值
    setAllowRegistration(checked);
    setAllowRegistrationMutation.mutate({
      key: ALLOW_SELF_REGISTRATION_KEY,
      data: { value: checked ? "true" : "false" },
    });
  };

  const handleBeianSave = async () => {
    try {
      await Promise.all([
        setBeianMutation.mutateAsync({
          key: ICP_BEIAN_NO_KEY,
          data: { value: icpBeianNo.trim() },
        }),
        setBeianMutation.mutateAsync({
          key: POLICE_BEIAN_NO_KEY,
          data: { value: policeBeianNo.trim() },
        }),
      ]);
      toast.success(t("common.saved"));
    } catch {
      toast.error(t("common.saveFailed"));
    }
  };

  if (
    isFrontendUrlLoading ||
    isLoginDefaultRouteLoading ||
    isAllowRegistrationLoading ||
    isIcpBeianLoading ||
    isPoliceBeianLoading
  ) {
    return (
      <div className="py-8 text-center text-muted-foreground">
        {t("common.loading")}
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex flex-col gap-6">
        <div className="rounded-lg border p-4 space-y-4">
          <h2 className="font-medium">{t("system.title")}</h2>
          <div className="space-y-2">
            <Label>{t("system.frontendUrl")}</Label>
            <Input
              value={frontendUrl}
              onChange={(e) => setFrontendUrl(e.target.value)}
              placeholder="https://example.com"
            />
            <p className="text-xs text-muted-foreground">
              {t("system.frontendUrlDescription")}
            </p>
          </div>
          <Button
            onClick={handleFrontendUrlSave}
            disabled={setFrontendUrlMutation.isPending}
          >
            {setFrontendUrlMutation.isPending
              ? t("common.saving")
              : t("common.save")}
          </Button>
        </div>
        <div className="rounded-lg border p-4 space-y-4">
          <h2 className="font-medium">{t("system.login")}</h2>
          <div className="space-y-2">
            <Label htmlFor="login-default-route">
              {t("system.loginDefaultRoute")}
            </Label>
            <Input
              id="login-default-route"
              value={loginDefaultRoute}
              onChange={(e) => setLoginDefaultRoute(e.target.value)}
              placeholder="/workbench"
            />
            <p className="text-xs text-muted-foreground">
              {t("system.loginDefaultRouteDescription")}
            </p>
          </div>
          <Button
            onClick={handleLoginDefaultRouteSave}
            disabled={setLoginDefaultRouteMutation.isPending}
          >
            {setLoginDefaultRouteMutation.isPending
              ? t("common.saving")
              : t("common.save")}
          </Button>
        </div>
        <div className="rounded-lg border p-4 space-y-4">
          <h2 className="font-medium">{t("system.registration")}</h2>
          <div className="flex items-center justify-between space-y-2">
            <div className="space-y-1">
              <Label htmlFor="allow-self-registration">
                {t("system.allowSelfRegistration")}
              </Label>
              <p className="text-xs text-muted-foreground">
                {t("system.allowSelfRegistrationDescription")}
              </p>
            </div>
            <Switch
              id="allow-self-registration"
              aria-label={t("system.allowSelfRegistration")}
              checked={allowRegistration}
              onCheckedChange={handleAllowRegistrationChange}
              disabled={setAllowRegistrationMutation.isPending}
            />
          </div>
        </div>
        <div className="rounded-lg border p-4 space-y-4">
          <h2 className="font-medium">{t("system.beian")}</h2>
          <div className="space-y-2">
            <Label htmlFor="icp-beian-no">
              {t("system.icpBeianNo")}
            </Label>
            <Input
              id="icp-beian-no"
              value={icpBeianNo}
              onChange={(e) => setIcpBeianNo(e.target.value)}
              placeholder={t("system.icpPlaceholder")}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="police-beian-no">
              {t("system.policeBeianNo")}
            </Label>
            <Input
              id="police-beian-no"
              value={policeBeianNo}
              onChange={(e) => setPoliceBeianNo(e.target.value)}
              placeholder={t("system.policePlaceholder")}
            />
            <p className="text-xs text-muted-foreground">
              {t("system.beianDescription")}
            </p>
          </div>
          <Button
            onClick={handleBeianSave}
            disabled={setBeianMutation.isPending}
          >
            {setBeianMutation.isPending
              ? t("common.saving")
              : t("common.save")}
          </Button>
        </div>
        <PythonBinSection />
      </div>
    </div>
  );
}

export const Route = createFileRoute("/_app/settings/system")({
  component: SystemConfigPage,
});

/** 智能体工坊分区：代码工具 Python 解释器路径（SystemConfig agent-tool.pythonBin） */
function PythonBinSection() {
  const { t } = useTranslation("systemSettings");
  const { data, isLoading } = useSysGetConfig(AGENT_TOOL_PYTHON_BIN_KEY);
  const setMutation = useSysSetConfig({
    mutation: {
      onSuccess: () => {
        toast.success(t("common.saved"));
      },
      onError: () => {
        toast.error(t("common.saveFailed"));
      },
    },
  });
  const [value, setValue] = useState("");

  useEffect(() => {
    if (data) {
      setValue(String(data.value ?? ""));
    }
  }, [data]);

  return (
    <div className="rounded-lg border p-4 space-y-4">
      <h2 className="font-medium">{t("system.workshop")}</h2>
      <div className="space-y-2">
        <Label htmlFor="agent-tool-python-bin">
          {t("system.pythonPath")}
        </Label>
        <Input
          id="agent-tool-python-bin"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="/usr/bin/python3"
          className="font-mono"
          disabled={isLoading}
        />
        <p className="text-xs text-muted-foreground">
          {t("system.pythonPathDescription")}
        </p>
      </div>
      <Button onClick={() => setMutation.mutate({ key: AGENT_TOOL_PYTHON_BIN_KEY, data: { value: value.trim() } })} disabled={setMutation.isPending}>
        {setMutation.isPending
          ? t("common.saving")
          : t("common.save")}
      </Button>
    </div>
  );
}
